// SCADA Entegrasyonu — tüm etiketler tek tabloda, zaman aralığına göre.
//
// Kaynak/sayfa ağacı (EntegrasyonSayfasi) "kurulum" ekranı: hangi ajan neyi
// gönderiyor. Bu ekran ise "işletme" ekranı: tesisin AKAN bütün okuma noktaları
// tek listede, seçilen aralıkta ne yaptıklarıyla birlikte. Ağaçta bir etiketi
// bulmak için önce kaynağı, sonra sayfayı açmak gerekiyordu.
//
// YERLEŞİM (kullanıcı kararı): sol tablo %40, sağ detay %60. Detay alanı burada
// asıl iş — grafik ve istatistik oraya sığmalı; tablo yalnız seçim aracı.
//
// Aralık ve kovalama SUNUCUDA: `/etiketler` aralığın özetini, `/analiz` de
// kovalanmış seriyi veriyor. İstemci ham ölçüm indirip özetlemiyor — 30 günlük
// bir aralıkta bu 86 bin satır demek olurdu.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import EtiketDrawer from './EtiketDrawer'
import EtiketGrafigi from './EtiketGrafigi'
import DurumCizelgesi from './DurumCizelgesi'
import ToplamaBlogu from './ToplamaBlogu'
import { ZOOM_BEKLEME_MS, zoomPenceresi, type ZoomPenceresi } from './zoom'
import { kaliteRozeti, sonDegerMetni, yasMetni } from './bicim'
import {
  ARALIKLAR,
  hazirAralik,
  isodanYerelMetne,
  kovaEtiketi,
  yerelMetindenIso,
  type AralikAnahtari,
} from './zamanAraligi'
import { entegrasyon, type EtiketOzetSatiri, type Istatistik } from './api'

/** Akan veri: liste ve seçili analiz kendiliğinden tazelenir (ms). */
const TAZELEME = 30_000

/** Sol tablo %40, detay %60 — kullanıcı kararı. */
const YERLESIM = { gridTemplateColumns: 'minmax(320px, 40%) minmax(0, 1fr)' }

const TABLO: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  maxHeight: 'calc(100vh - 118px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

/** Sayı + birim; değer yoksa birim de yazılmaz ("— %" sıfır gibi okunuyor). */
function olcu(deger: number | null | undefined, basamak: number, birim?: string | null): string {
  if (deger === null || deger === undefined) return '—'
  return birim ? `${tr(deger, basamak)} ${birim}` : tr(deger, basamak)
}

/** Basamak sayısını büyüklükten seç — 0,004 bar ile 13.257 kWh aynı olamaz. */
function basamakSec(min: number | null, maks: number | null): number {
  if (min === null || maks === null) return 2
  const buyuk = Math.max(Math.abs(min), Math.abs(maks))
  if (buyuk >= 1000) return 0
  if (buyuk >= 10) return 1
  if (buyuk >= 1) return 2
  return 3
}

function sureMetni(saniye: number | null): string {
  if (saniye === null) return '—'
  if (saniye < 90) return `${tr(saniye, 1)} sn`
  if (saniye < 5400) return `${tr(saniye / 60, 1)} dk`
  return `${tr(saniye / 3600, 1)} sa`
}

// ------------------------------------------------------------------ istatistik

function IstatistikTablosu({
  ist,
  birim,
}: {
  ist: Istatistik
  birim: string | null
}) {
  const b = basamakSec(ist.min, ist.maks)

  // İki sütun çifti: 60'lık alanda tek çift yarıyı boş bırakıyordu.
  const satirlar: [string, string, string, string][] = [
    ['En az', olcu(ist.min, b, birim), 'Ortalama', olcu(ist.ortalama, b, birim)],
    ['En çok', olcu(ist.maks, b, birim), 'Medyan', olcu(ist.medyan, b, birim)],
    ['Yayılım (maks−min)', olcu(ist.yayilim, b, birim), 'Std. sapma', olcu(ist.stdSapma, b, birim)],
    ['İlk değer', olcu(ist.ilk, b, birim), 'Son değer', olcu(ist.son, b, birim)],
    ['Fark (son−ilk)', olcu(ist.fark, b, birim), 'Ölçüm', tr(ist.adet, 0)],
    [
      'İlk ölçüm',
      ist.ilkZaman ? trTarihSaat(ist.ilkZaman) : '—',
      'Son ölçüm',
      ist.sonZaman ? trTarihSaat(ist.sonZaman) : '—',
    ],
    [
      'Örnekleme (ort.)',
      sureMetni(ist.ortOrneklemeSn),
      'Sayısal okuma',
      `${tr(ist.sayisalAdet, 0)} / ${tr(ist.adet, 0)}`,
    ],
  ]

  return (
    <table className="grid">
      <thead>
        <tr>
          <th style={{ width: '22%' }}>Gösterge</th>
          <th style={{ width: '28%' }}>Değer</th>
          <th style={{ width: '22%' }}>Gösterge</th>
          <th>Değer</th>
        </tr>
      </thead>
      <tbody>
        {satirlar.map(([a1, d1, a2, d2]) => (
          <tr key={a1}>
            <td>{a1}</td>
            <td className="mono" style={{ fontWeight: 600 }}>{d1}</td>
            <td>{a2}</td>
            <td className="mono" style={{ fontWeight: 600 }}>{d2}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DagilimTablosu({
  baslik,
  satirlar,
  toplam,
  rozet,
}: {
  baslik: string
  satirlar: { deger: string; adet: number }[]
  toplam: number
  /** Kalite dağılımında değerin rozet karşılığı gösterilsin mi. */
  rozet?: boolean
}) {
  if (satirlar.length === 0) return null
  return (
    <>
      <div className="sect-t">{baslik}</div>
      <table className="grid">
        <thead>
          <tr>
            <th>Değer</th>
            <th className="num" style={{ width: 96 }}>Adet</th>
            <th className="num" style={{ width: 84 }}>Pay</th>
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => {
            const k = rozet ? kaliteRozeti(s.deger) : null
            return (
              <tr key={s.deger}>
                <td>
                  {k ? (
                    <span className={`badge ${k.sinif}`}>{k.etiket}</span>
                  ) : (
                    <span className="mono">{s.deger}</span>
                  )}
                </td>
                <td className="num">{tr(s.adet, 0)}</td>
                <td className="num">{toplam > 0 ? `%${tr((s.adet / toplam) * 100, 1)}` : '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

// ----------------------------------------------------------------- detay alanı

/**
 * Durum etiketinin zaman çizelgesi bloğu.
 *
 * Ayrı sorgu, ayrı bileşen: çizelge ucu analizden pahalı ve yalnız durum
 * taşıyan etikette çağrılmalı. `refetchInterval` bilerek analizin iki katı —
 * her açık panel için 30 saniyede bir bu sorguyu koşturmak gereksiz yük, ve
 * durum çizelgesi zaten yavaş değişen bir görünüm.
 */
function DurumBlogu({
  etiketId,
  bas,
  son,
  zoomVar,
  zoomIste,
  zoomSifirla,
}: {
  etiketId: string
  bas: string
  son: string
  zoomVar: boolean
  zoomIste: (bas: number, son: number) => void
  zoomSifirla: () => void
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['entegrasyon-cizelge', etiketId, bas, son],
    queryFn: () => entegrasyon.cizelge(etiketId, { bas, son }),
    refetchInterval: TAZELEME * 2,
    // Yakınlaştırma pencereyi değiştiriyor; grafiğin her seferinde boşalıp
    // "Çizelge alınıyor…" yazması jesti tutuk gösteriyordu. Önceki çizelge
    // yerinde kalıp yenisi üstüne biniyor.
    placeholderData: (onceki) => onceki,
  })

  if (isLoading) {
    return (
      <div className="cell-sub" style={{ padding: 24, textAlign: 'center' }}>
        Çizelge alınıyor…
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="info-box" style={{ margin: 12 }}>
        <b>Çizelge alınamadı.</b>
        <span>
          Durum dağılımı tablosu aşağıda duruyor — hangi durumun ne kadar
          sürdüğü oradan okunabilir.
        </span>
      </div>
    )
  }

  if (data.bantlar.length === 0) {
    return (
      <div className="info-box" style={{ margin: 12 }}>
        <b>Bu aralıkta durum okuması yok.</b>
        <span>
          Etiket renk-durum okuması taşıyor ama seçilen pencerede hiç örnek
          gelmemiş — aralığı genişletin ya da ajanın aktığını Akış sekmesinden
          doğrulayın.
        </span>
      </div>
    )
  }

  return (
    <DurumCizelgesi
      cizelge={data}
      zoomVar={zoomVar}
      zoomIste={zoomIste}
      zoomSifirla={zoomSifirla}
    />
  )
}

function Detay({
  etiketId,
  bas,
  son,
  yazabilir,
  zoomVar,
  zoomIste,
  zoomSifirla,
}: {
  etiketId: string
  bas: string
  son: string
  yazabilir: boolean
  zoomVar: boolean
  zoomIste: (bas: number, son: number) => void
  zoomSifirla: () => void
}) {
  const { ac } = useDrawer()

  const { data: analiz, isLoading } = useQuery({
    queryKey: ['entegrasyon-analiz', etiketId, bas, son],
    queryFn: () => entegrasyon.analiz(etiketId, { bas, son }),
    refetchInterval: TAZELEME,
    // Aralık/yakınlaştırma değişiminde panel boşalmasın — bkz. çizelge.
    placeholderData: (onceki) => onceki,
  })

  if (isLoading || !analiz) {
    return (
      <div className="card">
        <div className="insp-empty">Yükleniyor…</div>
      </div>
    )
  }

  const e = analiz.etiket
  const ist = analiz.istatistik
  const kalite = kaliteRozeti(e.sonKalite)

  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div className="insp-h" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="insp-eyebrow">
            <span className="kod">{analiz.kaynakKod}</span>
            <span>·</span>
            <span className="kod">{analiz.sayfaKodu}</span>
            <span>·</span>
            <span className="mono">{e.anahtar}</span>
          </div>
          <div className="insp-t">
            {e.ad ?? e.anahtar}{' '}
            <span className={`badge ${e.tur === 'sayisal' ? 'b-navy' : 'b-gray'}`}>
              {e.tur === 'sayisal' ? 'Sayısal' : 'Durum'}
            </span>{' '}
            <span className={`badge ${kalite.sinif}`}>{kalite.etiket}</span>
            {e.uniteId && <span className="badge b-navy">Üniteye bağlı</span>}
          </div>
          <div className="cell-sub" style={{ marginTop: 2 }}>
            Son değer <b>{sonDegerMetni(e)}</b>
            {e.birim ? ` ${e.birim}` : ''} · {yasMetni(e.sonZamanYasiSn)} önce ·
            aralıkta {tr(ist.adet, 0)} ölçüm
          </div>
        </div>
        <button
          className="btn btn-sec btn-sm"
          type="button"
          style={{ flexShrink: 0 }}
          title="Adlandır · birim · üniteye ve beyan parametresine bağla"
          onClick={() =>
            ac({
              baslik: e.ad ?? e.anahtar,
              altBaslik: `${analiz.sayfaKodu} · ${e.anahtar}`,
              boyut: 'md',
              icerik: <EtiketDrawer etiket={e} yazabilir={yazabilir} />,
            })
          }
        >
          Düzenle
        </button>
      </div>

      {ist.adet === 0 ? (
        <div className="insp-empty">
          Bu aralıkta ölçüm yok — aralığı genişletin ya da ajanın aktığını Akış
          sekmesinden doğrulayın.
        </div>
      ) : (
        <>
          {/* Dal ETİKET TİPİNE bakıyor, ölçüm sayısına değil.
              `ist.sayisalAdet > 0` ölçütü sayısal bir etiketin pencere
              içindeki tüm okumaları okunamamışsa (`----` → sayi NULL) da
              durum dalına düşürüyordu — btys_test'te tam olarak böyle 24
              satır ölçüldü. O etiket durum çizelgesi çağırırdı ve boş
              çizelge alırdı. */}
          {e.tur === 'durum' ? (
            <DurumBlogu
              etiketId={etiketId}
              bas={bas}
              son={son}
              zoomVar={zoomVar}
              zoomIste={zoomIste}
              zoomSifirla={zoomSifirla}
            />
          ) : ist.sayisalAdet > 0 ? (
            <EtiketGrafigi
              analiz={analiz}
              zoomVar={zoomVar}
              zoomIste={zoomIste}
              zoomSifirla={zoomSifirla}
            />
          ) : (
            <div className="info-box" style={{ margin: 12 }}>
              <b>Bu etiket sayı taşımıyor.</b>
              <span>
                Renk-durum okuması (paket <span className="mono">distance</span> alanıyla
                geliyor, §3.2) — zaman serisi grafiği yerine aşağıdaki durum dağılımına bakın.
              </span>
            </div>
          )}

          {/* Toplama yalnız SAYISAL etikette: renk-durum okumasının toplamı
              yoktur (uç da 409 ile reddediyor). */}
          {e.tur === 'sayisal' && <ToplamaBlogu etiketId={etiketId} bas={bas} son={son} />}

          <div className="sect-t">Aralık İstatistiği</div>
          <IstatistikTablosu ist={ist} birim={e.birim} />

          {/* Sayısal etikette değer dağılımı binlerce satır olurdu; sunucu bu
              yüzden yalnız sayısı OLMAYAN ölçümleri (renk-durum) sayıyor. */}
          <DagilimTablosu baslik="Durum Dağılımı" satirlar={ist.durum} toplam={ist.adet} />
          <DagilimTablosu
            baslik="Kalite Dağılımı"
            satirlar={ist.kalite}
            toplam={ist.adet}
            rozet
          />

          <div className="rule">
            <b>Aralık</b>
            <span>
              {trTarihSaat(analiz.bas)} → {trTarihSaat(analiz.son)} ·{' '}
              {tr(analiz.kovalar.length, 0)} kova × {kovaEtiketi(analiz.kovaSn)}
              {ist.sayisalAdet < ist.adet && (
                <>
                  {' · '}
                  <b>{tr(ist.adet - ist.sayisalAdet, 0)}</b> ölçümün sayısı yok
                  (okunamadı ya da renk-durum)
                </>
              )}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------- ekran

function Icerik() {
  const git = useNavigate()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('Entegrasyon', 'yaz')

  const [aralikAnahtari, setAralikAnahtari] = useState<AralikAnahtari>('24s')
  const [serbest, setSerbest] = useState<{ bas: string; son: string }>(() => {
    const a = hazirAralik(24)
    return { bas: isodanYerelMetne(a.bas), son: isodanYerelMetne(a.son) }
  })
  const [arama, setArama] = useState('')
  const [secim, setSecim] = useState<string | null>(null)

  /**
   * Hazır aralıkta bitiş "şimdi" olduğu için her render'da yeni bir değer
   * çıkardı ve sorgu anahtarı sürekli değişip listeyi döngüye sokardı. Bu
   * yüzden aralık, aralık seçimi değişince BİR KEZ hesaplanıyor; tazeleme
   * `refetchInterval` ile yapılıyor.
   */
  const [damga, setDamga] = useState(() => Date.now())
  useEffect(() => setDamga(Date.now()), [aralikAnahtari])

  const aralik = useMemo(() => {
    if (aralikAnahtari === 'serbest') {
      const b = yerelMetindenIso(serbest.bas)
      const s = yerelMetindenIso(serbest.son)
      if (b && s) return b <= s ? { bas: b, son: s } : { bas: s, son: b }
      return hazirAralik(24, new Date(damga))
    }
    const saat = ARALIKLAR.find((a) => a.anahtar === aralikAnahtari)?.saat ?? 24
    return hazirAralik(saat, new Date(damga))
  }, [aralikAnahtari, serbest, damga])

  /**
   * Grafik üzerinden yapılan YAKINLAŞTIRMA. `aralik` kullanıcının SEÇTİĞİ
   * pencere olarak kalıyor; zoom onu ezmiyor, üstüne biniyor. Böylece
   * "Sıfırla" tek satır: setZoom(null).
   *
   * Zoom salt görsel DEĞİL — pencereyi değiştirip veriyi yeniden çektiriyor.
   * Sebep: sunucu bant sayısı sınırı aşınca kova moduna düşüyor ve orada bant
   * "o kovanın baskın durumu", yani yaklaşık. Salt görsel bir yakınlaştırma o
   * yaklaşık bandı büyütüp gerçek bir geçişmiş gibi gösterirdi. Yeniden çekim
   * yakınlaşınca bant sayısını düşürüyor ve sunucu kendiliğinden gerçek
   * geçişlere (aralik moduna) dönüyor — taşma uyarısı da kayboluyor.
   *
   * Maliyet itirazının cevabı: zoom yalnızca DARALTIYOR, yani her zoom
   * sorgusu hâlihazırda 30 sn'de bir koşan ön ayar sorgusundan ucuz.
   */
  const [zoom, setZoom] = useState<ZoomPenceresi | null>(null)

  // Ön ayar ya da serbest aralık değişince zoom düşer: kullanıcı pencereyi
  // yeniden tanımlamıştır, eski yakınlaştırma onun dışında kalabilir.
  useEffect(() => setZoom(null), [aralikAnahtari, serbest.bas, serbest.son])

  const zoomZamanlayici = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (zoomZamanlayici.current) clearTimeout(zoomZamanlayici.current)
  }, [])

  /**
   * Grafikten gelen ham jest. Tekerlek bir seride onlarca olay üretiyor;
   * bekleme olmadan her biri ayrı bir sorgu tetiklerdi.
   */
  const zoomIste = useCallback(
    (istenenBas: number, istenenSon: number) => {
      const sinir = { bas: Date.parse(aralik.bas), son: Date.parse(aralik.son) }
      const p = zoomPenceresi({ bas: istenenBas, son: istenenSon }, sinir)
      if (zoomZamanlayici.current) clearTimeout(zoomZamanlayici.current)
      zoomZamanlayici.current = setTimeout(() => setZoom(p), ZOOM_BEKLEME_MS)
    },
    [aralik.bas, aralik.son],
  )

  /**
   * Sıfırla: ön ayara dön VE pencereyi tazele. `damga` da yenileniyor çünkü
   * aynı segmente yeniden basmak React'ın bail-out'una takılıyor ve kullanıcının
   * pencereyi ilerletmek için başka kaçış yolu kalmıyordu.
   */
  const zoomSifirla = useCallback(() => {
    if (zoomZamanlayici.current) clearTimeout(zoomZamanlayici.current)
    setZoom(null)
    // Pencere BİLEREK tazelenmiyor. Bir ara `damga` da yenileniyordu; o,
    // aralığı değiştirdiği için 12 binlik etiket listesi dahil her sorguyu
    // yeniden çektiriyor ve sıfırlama sayfa yenilemesi gibi görünüyordu.
    // Sıfırlama yalnız yakınlaştırmayı kaldırır.
  }, [])

  /** Grafiklerin ve detay sorgularının gerçekten kullandığı pencere. */
  const etkinAralik = useMemo(
    () =>
      zoom
        ? { bas: new Date(zoom.bas).toISOString(), son: new Date(zoom.son).toISOString() }
        : aralik,
    [zoom, aralik],
  )

  const { data: etiketler = [], isLoading } = useQuery({
    queryKey: ['entegrasyon-tum-etiketler', aralik.bas, aralik.son],
    queryFn: () => entegrasyon.tumEtiketler({ bas: aralik.bas, son: aralik.son }),
    refetchInterval: TAZELEME,
    // Anahtar değiştiğinde (aralık değişimi, dakika sınırı) liste BOŞALMIYOR:
    // önceki sonuç yerinde kalıyor, yenisi arka planda geliyor. Boşalma
    // kullanıcıya sayfanın yenilendiğini düşündürüyordu — 12 binlik bir liste
    // için bu, ekranın bir anlığına çökmesi demek.
    placeholderData: (onceki) => onceki,
  })

  // Arama istemcide: liste tesis başına birkaç yüz satır, sunucuya her tuşta
  // gitmek gereksiz gecikme olurdu. Süzgeç sunucuda da var (q) ama burada
  // kullanılmıyor.
  const liste = useMemo(() => {
    if (!arama.trim()) return etiketler
    const a = arama.trim().toLocaleLowerCase('tr')
    return etiketler.filter((e) =>
      [e.anahtar, e.ad ?? '', e.birim ?? '', e.kaynakKod, e.kaynakAd, e.sayfaKodu, e.sayfaAd]
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(a),
    )
  }, [etiketler, arama])

  // Seçim listeden düşerse (arama daraldı, aralık değişti) ilk satıra kayar —
  // detay alanı boş kalıp kullanıcıyı "bozuldu mu" diye düşündürmesin.
  const ilkId = liste[0]?.id
  useEffect(() => {
    setSecim((s) => (s && liste.some((e) => e.id === s) ? s : (ilkId ?? null)))
  }, [ilkId, liste])

  const akan = etiketler.filter((e) => e.adet > 0).length

  return (
    <>
      <div className="crumb">
        Ana Veri <b>/ SCADA Entegrasyonu / Etiketler</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">SCADA Etiketleri — Zaman Serisi ve İstatistik</h1>
        <div className="page-actions">
          <div className="seg">
            <button className="seg-i" type="button" onClick={() => git('/entegrasyon')}>
              Kaynaklar
            </button>
            <button className="seg-i on" type="button">
              Etiketler
              <small>{etiketler.length}</small>
            </button>
          </div>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Etiket</div>
          <div className="k-v">{tr(etiketler.length, 0)}</div>
          <div className="k-s">tesisin tüm okuma noktaları</div>
        </div>
        <div className="k">
          <div className="k-l">Aralıkta Akan</div>
          <div className="k-v">{tr(akan, 0)}</div>
          <div className="k-s">{tr(etiketler.length - akan, 0)} etiket sessiz</div>
        </div>
        <div className="k">
          <div className="k-l">Ölçüm</div>
          <div className="k-v">{tr(etiketler.reduce((t, e) => t + e.adet, 0), 0)}</div>
          <div className="k-s">seçilen aralıkta</div>
        </div>
        <div className="k">
          <div className="k-l">Sayısal</div>
          <div className="k-v">{tr(etiketler.filter((e) => e.tur === 'sayisal').length, 0)}</div>
          <div className="k-s">
            {tr(etiketler.filter((e) => e.tur === 'durum').length, 0)} renk-durum
          </div>
        </div>
        <div className="k">
          <div className="k-l">Üniteye Bağlı</div>
          <div className="k-v">{tr(etiketler.filter((e) => e.uniteId).length, 0)}</div>
          <div className="k-s">beyana akabilir</div>
        </div>
      </div>

      <div className="list-grid" style={YERLESIM}>
        <div className="card" style={TABLO}>
          <div className="toolbar">
            <div className="seg">
              {ARALIKLAR.map((a) => (
                <button
                  key={a.anahtar}
                  type="button"
                  className={aralikAnahtari === a.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => setAralikAnahtari(a.anahtar)}
                >
                  {a.etiket}
                </button>
              ))}
            </div>
          </div>

          {aralikAnahtari === 'serbest' && (
            <div className="toolbar">
              <div className="fld" style={{ flex: 1, minWidth: 0 }}>
                <label>Başlangıç</label>
                <input
                  className="in mono"
                  type="datetime-local"
                  value={serbest.bas}
                  onChange={(e) => setSerbest((o) => ({ ...o, bas: e.target.value }))}
                />
              </div>
              <div className="fld" style={{ flex: 1, minWidth: 0 }}>
                <label>Bitiş</label>
                <input
                  className="in mono"
                  type="datetime-local"
                  value={serbest.son}
                  onChange={(e) => setSerbest((o) => ({ ...o, son: e.target.value }))}
                />
              </div>
            </div>
          )}

          <div className="toolbar">
            <div className="f-search" style={{ width: '100%' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Etiket, ad, kaynak, sayfa ara…"
                value={arama}
                onChange={(e) => setArama(e.target.value)}
              />
            </div>
          </div>

          <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table className="grid" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Etiket</th>
                  <th className="num" style={{ width: 86 }}>Son</th>
                  <th className="num" style={{ width: 82 }}>Ort.</th>
                  <th className="num" style={{ width: 62 }}>Ölçüm</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={4} className="cell-sub" style={{ padding: 14 }}>
                      Yükleniyor…
                    </td>
                  </tr>
                )}
                {!isLoading && liste.length === 0 && (
                  <tr>
                    <td colSpan={4} className="cell-sub" style={{ padding: 14 }}>
                      {etiketler.length === 0
                        ? 'Henüz etiket yok — ilk pakette kendiliğinden açılırlar.'
                        : 'Süzgece uyan etiket yok.'}
                    </td>
                  </tr>
                )}

                {liste.map((e) => (
                  <Satir
                    key={e.id}
                    etiket={e}
                    secili={secim === e.id}
                    sec={() => setSecim(e.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid-foot" style={{ borderTop: '1px solid var(--line-soft)' }}>
            <span>
              {tr(liste.length, 0)} etiket · sayılar seçilen aralığa ait
            </span>
          </div>
        </div>

        {secim ? (
          <Detay
            etiketId={secim}
            bas={etkinAralik.bas}
            son={etkinAralik.son}
            yazabilir={yazabilir}
            zoomVar={zoom !== null}
            zoomIste={zoomIste}
            zoomSifirla={zoomSifirla}
          />
        ) : (
          <div className="card">
            <div className="insp-empty">Soldan bir etiket seçin.</div>
          </div>
        )}
      </div>
    </>
  )
}

function Satir({
  etiket: e,
  secili,
  sec,
}: {
  etiket: EtiketOzetSatiri
  secili: boolean
  sec: () => void
}) {
  const b = basamakSec(e.min, e.maks)
  return (
    <tr className={secili ? 'sel' : ''} style={{ cursor: 'pointer' }} onClick={sec}>
      <td>
        <div className="cell-name">
          {e.ad ?? e.anahtar}
          {e.adet === 0 && (
            <span className="badge b-gray" title="Seçilen aralıkta hiç ölçüm yok">
              Sessiz
            </span>
          )}
          {e.uniteKod && <span className="badge b-navy">{e.uniteKod}</span>}
        </div>
        <div className="cell-sub" style={{ overflowWrap: 'anywhere' }}>
          <span className="kod">{e.kaynakKod}</span> · <span className="kod">{e.sayfaKodu}</span> ·{' '}
          <span className="mono">{e.anahtar}</span>
        </div>
      </td>
      {/* Ham metin yeniden biçimlendirilmiyor (§3.2): operatör SCADA ekranında
          ne görüyorsa burada da onu görmeli. */}
      <td className="num" style={{ fontWeight: 600 }}>
        {sonDegerMetni(e)}
      </td>
      <td className="num">{olcu(e.ortalama, b)}</td>
      <td className="num">{tr(e.adet, 0)}</td>
    </tr>
  )
}

export default function EtiketlerSayfasi() {
  return (
    <Kabuk listeBasligi="SCADA Etiketleri">
      <YetkiKapisi modul="Entegrasyon">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
